import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import {
  Row, Col, Tag, Typography, Button,
} from 'antd';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AlumlyButton from '../../../components/AlumlyButton';
import { RouteNameEnum } from '../../../enums/routeName';
import { PathBuilder } from '../../../utils/pathBuilder';
import usePermission from '../../../hooks/usePermission';
import { PermissionEnum } from '../../../enums/permission';
import useServices from '../../../hooks/useServices';
import { ServiceNameEnum } from '../../../enums/serviceName';
import ConfirmModal from './ConfirmModal';
import deleteIcon from '../../../assets/images/delete-icon.svg';

export type AlumniDetailT = {
  faculty: string | null;
  graduationYear: number | null;
  batch: number | null;
  studentId: string | null;
  preferredName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  companyNameTh?: string | null;
  website?: string | null;
  taxId?: string | null;
  mapUrl?: string | null;
  companyLogoURL?: string | null;
  homeAddress: string | null;
  homeAddressLine: string | null;
  homeSubDistrict: string | null;
  homeDistrict: string | null;
  homeProvince: string | null;
  homePostalCode: string | null;
  workAddress: string | null;
  workAddressLine: string | null;
  workSubDistrict: string | null;
  workDistrict: string | null;
  workProvince: string | null;
  workPostalCode: string | null;
  isDirectoryVisible: boolean;
  alumniStatus?: string;
  userType?: 'alumni' | 'bba_student' | 'faculty' | 'employer';
  isOpenToWork?: boolean;
  industry?: string | null;
  skills?: string[] | null;
  cvURL?: string | null;
  cvFileName?: string | null;
};

interface AlumniDetailCardProps {
  alumniDetail: AlumniDetailT;
  refetchData?: () => void;
}

const statusColor: Record<string, string> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const statusLabel: Record<string, string> = {
  pending: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const userTypeLabel: Record<string, string> = {
  alumni: 'Alumni',
  bba_student: 'BBA Student',
  faculty: 'Faculty',
  employer: 'Employer',
};

const AlumniDetailCard: React.FC<AlumniDetailCardProps> = (props: AlumniDetailCardProps) => {
  const { alumniDetail, refetchData } = props;
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const { id: customerId } = useParams();
  const [loading, setLoading] = useState(false);
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const {
    faculty,
    graduationYear,
    batch,
    studentId,
    preferredName,
    jobTitle,
    companyName,
    companyNameTh,
    website,
    taxId,
    mapUrl,
    companyLogoURL,
    homeAddressLine,
    homeSubDistrict,
    homeDistrict,
    homeProvince,
    homePostalCode,
    workAddressLine,
    workSubDistrict,
    workDistrict,
    workProvince,
    workPostalCode,
    isDirectoryVisible,
    alumniStatus = 'pending',
    userType = 'alumni',
    isOpenToWork = false,
    industry,
    skills,
    cvURL,
    cvFileName,
  } = alumniDetail;
  const isEmployer = userType === 'employer';
  // Label everything generically for employers, who aren't alumni.
  const subjectLabel = isEmployer ? 'employer' : 'applicant';

  const { service: approveService } = useServices(ServiceNameEnum.customerMgmt.approveAlumni);
  const { service: rejectService } = useServices(ServiceNameEnum.customerMgmt.rejectAlumni);
  const { service: deleteCvService } = useServices(ServiceNameEnum.customerMgmt.deleteCv);

  const handleRemoveCv = async () => {
    setLoading(true);
    const { error } = await deleteCvService.delete({ params: { id: customerId } });
    setLoading(false);
    setCvConfirmOpen(false);
    if (!error) {
      toast.success('CV removed successfully');
      refetchData?.();
    } else {
      toast.error('Failed to remove CV');
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    const { error } = await approveService.post({}, { params: { id: customerId } });
    setLoading(false);
    if (!error) {
      toast.success(`${isEmployer ? 'Employer' : 'Alumni'} approved successfully`);
      refetchData?.();
    } else {
      toast.error(`Failed to approve ${subjectLabel}`);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    const { error } = await rejectService.post({}, { params: { id: customerId } });
    setLoading(false);
    if (!error) {
      toast.success(`${isEmployer ? 'Employer' : 'Alumni'} rejected`);
      refetchData?.();
    } else {
      toast.error(`Failed to reject ${subjectLabel}`);
    }
  };

  return (
    <div style={{
      padding: 24,
      paddingBottom: 40,
      borderRadius: '12px',
      boxShadow: '2px 2px 27px 0px rgba(0, 0, 0, 0.08)',
      height: '100%',
    }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
      }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}
        >
          <Typography.Title level={4} style={{ fontWeight: 700, margin: 0, whiteSpace: 'nowrap' }}>
            {isEmployer ? 'Employer details' : 'Alumni details'}
          </Typography.Title>
          <Tag color={statusColor[alumniStatus] || 'warning'}>
            {statusLabel[alumniStatus] || 'Pending'}
          </Tag>
          <Tag>
            {userTypeLabel[userType] || 'Alumni'}
          </Tag>
          {isOpenToWork && (
            <Tag color="processing">Open to work</Tag>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {checkPermission([PermissionEnum.code.customerEdit]) && (
            <AlumlyButton
              label="rxvWhite"
              icon={<EditIcon />}
              width={200}
              onClick={() => navigate(
                PathBuilder.buildPath(RouteNameEnum.customer.editAlumni, { params: { id: customerId } }),
              )}
            >
              <span style={{ fontWeight: 700 }}>
                {isEmployer ? 'Edit employer details' : 'Edit alumni details'}
              </span>
            </AlumlyButton>
          )}
        </div>
      </div>

      {/* Alumni Approval Actions */}
      {alumniStatus !== 'approved' && checkPermission([PermissionEnum.code.customerApproveAlumni]) && (
        <div style={{
          marginTop: 16, padding: 16, borderRadius: '8px', backgroundColor: alumniStatus === 'pending' ? '#FFF8E1' : '#FFEBEE', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
        >
          <Typography.Text strong>
            {alumniStatus === 'pending'
              ? `This ${subjectLabel} is waiting for approval`
              : `This ${subjectLabel} was rejected`}
          </Typography.Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
              disabled={loading}
              onClick={handleApprove}
              style={{ backgroundColor: '#2E7D32', borderColor: '#2E7D32' }}
            >
              Approve
            </Button>
            {alumniStatus !== 'rejected' && (
              <Button
                danger
                size="small"
                icon={<CancelIcon style={{ fontSize: 16 }} />}
                disabled={loading}
                onClick={handleReject}
              >
                Reject
              </Button>
            )}
          </div>
        </div>
      )}

      <Row gutter={[8, 16]} style={{ marginTop: 16 }}>
        {isEmployer ? (
          <>
            {/* Employer (company) account — admin verifies these to approve. */}
            <Col md={24} xs={24}>
              <Typography.Text type="secondary">Company logo</Typography.Text>
              {companyLogoURL ? (
                <div>
                  <img
                    src={companyLogoURL}
                    alt="Company logo"
                    style={{
                      marginTop: 4,
                      width: 72,
                      height: 72,
                      borderRadius: '12px',
                      objectFit: 'cover',
                      border: '1px solid #eee',
                    }}
                  />
                </div>
              ) : (
                <div>-</div>
              )}
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Company name (EN)</Typography.Text>
              <div>{companyName || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Company name (TH)</Typography.Text>
              <div>{companyNameTh || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Company / Tax ID</Typography.Text>
              <div>{taxId || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Industry</Typography.Text>
              <div>{industry || '-'}</div>
            </Col>
            <Col md={24} xs={24}>
              <Typography.Text type="secondary">Website / LinkedIn</Typography.Text>
              {website ? (
                <div>
                  <a
                    href={/^https?:\/\//.test(website) ? website : `https://${website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {website}
                  </a>
                </div>
              ) : (
                <div>-</div>
              )}
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Contact person (HR)</Typography.Text>
              <div>{preferredName || jobTitle || '-'}</div>
            </Col>
            <Col md={24} xs={24}>
              <Typography.Text type="secondary">Company location (Google Maps)</Typography.Text>
              {mapUrl ? (
                <div>
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              ) : (
                <div>-</div>
              )}
            </Col>
          </>
        ) : (
          <>
            {/* Faculty field hidden — omitted from display */}
            {/* <Col md={12} xs={24}>
              <Typography.Text type="secondary">Faculty</Typography.Text>
              <div>{faculty || '-'}</div>
            </Col> */}
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">SHI</Typography.Text>
              <div>{graduationYear || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">รุ่นที่ BBA</Typography.Text>
              <div>{batch || '-'}</div>
            </Col>
            {/* Student ID field hidden — omitted from display */}
            {/* <Col md={12} xs={24}>
              <Typography.Text type="secondary">Student ID</Typography.Text>
              <div>{studentId || '-'}</div>
            </Col> */}
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Preferred name</Typography.Text>
              <div>{preferredName || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Job title</Typography.Text>
              <div>{jobTitle || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Company</Typography.Text>
              <div>{companyName || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Industry</Typography.Text>
              <div>{industry || '-'}</div>
            </Col>
            <Col md={12} xs={24}>
              <Typography.Text type="secondary">Skills</Typography.Text>
              <div>{(skills && skills.length > 0) ? skills.join(', ') : '-'}</div>
            </Col>
            <Col md={24} xs={24}>
              <Typography.Text type="secondary">CV / Resume</Typography.Text>
              {cvURL ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 4 }}>
                  <Button
                    size="small"
                    href={cvURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cvFileName ? `View CV (${cvFileName})` : 'View CV'}
                  </Button>
                  {checkPermission([PermissionEnum.code.customerApproveAlumni]) && (
                    <Button
                      danger
                      size="small"
                      disabled={loading}
                      onClick={() => setCvConfirmOpen(true)}
                    >
                      Remove CV
                    </Button>
                  )}
                </div>
              ) : (
                <div>-</div>
              )}
            </Col>
            <Col md={24} xs={24}>
              <Typography.Text type="secondary">Home address</Typography.Text>
              <div>
                {[homeAddressLine, homeSubDistrict, homeDistrict, homeProvince, homePostalCode]
                  .filter(Boolean).join(', ') || '-'}
              </div>
            </Col>
          </>
        )}
        <Col md={24} xs={24}>
          <Typography.Text type="secondary">Work address</Typography.Text>
          <div>
            {[workAddressLine, workSubDistrict, workDistrict, workProvince, workPostalCode]
              .filter(Boolean).join(', ') || '-'}
          </div>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={cvConfirmOpen}
        icon={deleteIcon}
        message="Are you sure you want to remove this CV?"
        labelConfirm="Remove CV"
        labelCancel="Cancel"
        handleConfirm={handleRemoveCv}
        handleClose={() => setCvConfirmOpen(false)}
      />
    </div>
  );
};

export default AlumniDetailCard;
